import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import get from 'lodash/get';
import { isWindows81 } from '../../utils/browser-utils';
import { callSelectors, featureSelectors, streamActions } from '@lifesize/nucleus';
import { FLAG_4K_RESOLUTION } from 'constants/FeatureFlags';
import QualityDropdown from 'components/SourceDisplay/QualityDropdown';
import { highQualityHardwareConcurrencyLimit } from 'constants/Application';
import { getCameraMaxFrameRate } from 'utils/mediaConstraints';

interface ConstraintList {
  // eslint-disable-next-line no-undef
  [propName: string]: ConstrainULongRange;
}

const messages = defineMessages({
  FourK: { id: 'components.qualitySelect.FourK', defaultMessage: '4k - Auto fps' },
  HDfull: { id: 'components.qualitySelect.HDfull', defaultMessage: '1080p - Auto fps' },
  HDfulllow: { id: 'components.qualitySelect.HDfulllow', defaultMessage: '1080p - 30fps' },
  HD: { id: 'components.qualitySelect.HD', defaultMessage: '720p - Auto fps' },
  HDlow: { id: 'components.qualitySelect.HDlow', defaultMessage: '720p - 30fps' },
  ws360p: { id: 'components.qualitySelect.ws360p', defaultMessage: '360p - Auto fps' } // ws (widescreen -> 16:9) 360p
});

const constraints: ConstraintList = {
  h4k: { ideal: 2160, max: 2160, min: 180 },
  w4k: { ideal: 3840, max: 3840, min: 320 },
  h1080: { ideal: 1080, max: 1080, min: 180 },
  w1080: { ideal: 1920, max: 1920, min: 320 },
  h720: { ideal: 720, max: 720, min: 180 },
  w720: { ideal: 1280, max: 1280, min: 320 },
  h360: { ideal: 360, max: 360, min: 180 },
  w360: { ideal: 640, max: 640, min: 320 }
};

// Remove ideal constraints for Windows 8.1, as it breaks some cameras during getUserMedia
if (isWindows81()) {
  Object.values(constraints).forEach(c => delete c.ideal);
}

const mapStateToProps = (state: any, { intl }) => {
  const qualityList = [
    { label: intl.formatMessage(messages.HDfull), width: constraints.w1080, height: constraints.h1080, frameRate: { max: getCameraMaxFrameRate() } },
    { label: intl.formatMessage(messages.HD), width: constraints.w720, height: constraints.h720, frameRate: { max: getCameraMaxFrameRate() } },
    { label: intl.formatMessage(messages.ws360p), width: constraints.w360, height: constraints.h360, frameRate: { max: getCameraMaxFrameRate() } },
  ];

  const fourKFeature = featureSelectors.getFeatureFlag(state)(FLAG_4K_RESOLUTION);
  if (fourKFeature) {
    qualityList.unshift({
      label: intl.formatMessage(messages.FourK), width: constraints.w4k, height: constraints.h4k, frameRate: { max: getCameraMaxFrameRate() }
    });
  }

  const primaryVideoSourceId = get(state, 'nucleus.media.selectedVideoSourceId') || get(state, 'nucleus.media.primaryVideoSourceId');
  const primaryAudioSourceId = get(state, 'nucleus.media.selectedAudioSourceId') || get(state, 'nucleus.media.primaryAudioSourceId');
  const primaryAudioOutputId = get(state, 'app.media.selectedAudioOutputId') || get(state, 'nucleus.media.primaryAudioOutputId');
  const selectedVideoConstraints = get(state, 'nucleus.media.selectedVideoConstraints');
  const defaultVideoConstraints = get(state, 'nucleus.media.defaultVideoConstraints');
  const currentQuality = selectedVideoConstraints ? selectedVideoConstraints : defaultVideoConstraints;
  const activeCall = callSelectors.activeCallSelector(state);
  const isAudioMuted = get(activeCall, 'isAudioMuted');
  const isVideoMuted = get(activeCall, 'isVideoMuted');
  const selectedSources = {
    primaryVideoSourceId,
    primaryAudioSourceId,
    primaryAudioOutputId
  };

  let selectedQuality;
  if (currentQuality) {
    selectedQuality = qualityList.find(q =>
      (q.width.max === currentQuality.width || q.width.max === get(currentQuality, 'width.max')) &&
      (q.height.max === currentQuality.height || q.height.max === get(currentQuality, 'height.max')));
  }

  if (!selectedQuality) {
    // Default the camera acquisition to 640x360 (360p) for processors with 4 virtual cores or less for webapp
    if (navigator.hardwareConcurrency <= highQualityHardwareConcurrencyLimit) {
      selectedQuality = qualityList[qualityList.length - 1];
    }
    selectedQuality = qualityList[0];
  }

  return {
    selectedQuality,
    isVideoMuted,
    isAudioMuted,
    selectedSources,
    qualityList
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  // eslint-disable-next-line no-undef
  selectVideoSource: (quality: ConstrainULong, sources: any) => {
    dispatch(streamActions.setVideoSourceQuality(quality));
    if (sources.video) {
      dispatch(streamActions.acquireLocalPrimaryStream(sources));
    }
  },
});

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => (
  Object.assign({}, ownProps, stateProps, dispatchProps,
    {
      selectVideoSource: (quality: any) => {
        dispatchProps.selectVideoSource(quality, Object.assign(
          {}, stateProps.selectedSources,
          { audio: !stateProps.isAudioMuted, video: !stateProps.isVideoMuted }
        ));
      }
    })
);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps, mergeProps)(QualityDropdown));
